import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UploadIcon from '@mui/icons-material/Upload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import {
  terraformApi,
  TerraformTemplate,
  TemplateVariable,
  OptionsSource,
  ModuleSummary,
  ModuleVariable,
  ProviderSchemaVersion,
} from '../services/terraform-api';
import {
  gitlabApi,
  GitLabInstance,
  GitLabProject,
  GitLabBranch,
  GitLabTreeItem,
} from '../services/gitlab-api';
import { rangeosApiService, RangeOSApiEndpoint } from '../services/rangeos-api';

const RANGEOS_RESOURCE_TYPES = [
  { key: 'vm-images', label: 'VM Images', defaultLabel: 'name', defaultValue: 'uuid' },
  { key: 'charts', label: 'Charts', defaultLabel: 'name', defaultValue: 'name' },
  { key: 'ansible-roles', label: 'Ansible Roles', defaultLabel: 'name', defaultValue: 'uuid' },
  { key: 'networks', label: 'Networks', defaultLabel: 'name', defaultValue: 'uuid' },
  { key: 'packages', label: 'Packages', defaultLabel: 'name', defaultValue: 'uuid' },
  { key: 'ghost-traffic', label: 'Ghost Traffic', defaultLabel: 'name', defaultValue: 'uuid' },
  { key: 'containers', label: 'Containers', defaultLabel: 'name', defaultValue: 'name' },
  { key: 'volumes', label: 'Volumes', defaultLabel: 'name', defaultValue: 'uuid' },
] as const;

interface TerraformTemplateEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editTemplate?: TerraformTemplate | null;
}

export const TerraformTemplateEditor: React.FC<TerraformTemplateEditorProps> = ({
  open,
  onClose,
  onSave,
  editTemplate,
}) => {
  // Template fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hclTemplate, setHclTemplate] = useState('');
  const [variables, setVariables] = useState<TemplateVariable[]>([]);

  // Linked module state
  const [linkedModule, setLinkedModule] = useState<string>('');
  const [modules, setModules] = useState<ModuleSummary[]>([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [optionInput, setOptionInput] = useState<Record<string, string>>({});

  // GitLab import state
  const [showGitLabBrowser, setShowGitLabBrowser] = useState(false);
  const [instances, setInstances] = useState<GitLabInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [projects, setProjects] = useState<GitLabProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | ''>('');
  const [branches, setBranches] = useState<GitLabBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [treeItems, setTreeItems] = useState<GitLabTreeItem[]>([]);
  const [treePath, setTreePath] = useState('');
  const [treeLoading, setTreeLoading] = useState(false);
  const [sourceFile, setSourceFile] = useState<TerraformTemplate['sourceFile'] | undefined>();

  // Template annotation detection
  const [detectedTemplateId, setDetectedTemplateId] = useState<string | null>(null);
  const [detectedTemplateName, setDetectedTemplateName] = useState<string | null>(null);

  // Dynamic options source state
  const [rangeosEndpoints, setRangeosEndpoints] = useState<RangeOSApiEndpoint[]>([]);
  const [schemaVersions, setSchemaVersions] = useState<ProviderSchemaVersion[]>([]);
  const [schemaResourceTypes, setSchemaResourceTypes] = useState<Record<string, string[]>>({});
  const [schemaFields, setSchemaFields] = useState<Record<string, string[]>>({});
  const [optionsPreview, setOptionsPreview] = useState<Record<string, Array<{ label: string; value: string }>>>({});
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({});
  const [swaggerSchemas, setSwaggerSchemas] = useState<Record<string, string[]>>({});
  const [swaggerEnumFields, setSwaggerEnumFields] = useState<Record<string, Array<{ field: string; values: string[] }>>>({});

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize when opening
  useEffect(() => {
    if (open) {
      setError(null);
      setOptionInput({});
      if (editTemplate) {
        setName(editTemplate.name);
        setDescription(editTemplate.description || '');
        setHclTemplate(editTemplate.hclTemplate);
        setVariables(editTemplate.variables);
        setLinkedModule(editTemplate.linkedModule || '');
        setSourceFile(editTemplate.sourceFile);
      } else {
        setName('');
        setDescription('');
        setHclTemplate('');
        setVariables([]);
        setLinkedModule('');
        setSourceFile(undefined);
      }
      setShowGitLabBrowser(false);
      // Load modules list
      if (!modulesLoaded) {
        terraformApi.listModules().then(m => {
          setModules(m);
          setModulesLoaded(true);
        }).catch(() => {});
      }
      // Load RangeOS endpoints and schema versions for dynamic options
      rangeosApiService.getEndpoints().then(setRangeosEndpoints).catch(() => {});
      terraformApi.listVersions().then(setSchemaVersions).catch(() => {});
    }
  }, [open, editTemplate]);

  // Extract placeholders from HCL template and sync with variables list
  const syncVariablesFromTemplate = useCallback((hcl: string) => {
    const placeholderRegex = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
    const found = new Set<string>();
    let match;
    while ((match = placeholderRegex.exec(hcl)) !== null) {
      found.add(match[1]);
    }

    setVariables(prev => {
      const existing = new Map(prev.map(v => [v.name, v]));
      const result: TemplateVariable[] = [];

      for (const name of found) {
        if (existing.has(name)) {
          result.push(existing.get(name)!);
        } else {
          result.push({
            name,
            label: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            type: 'string',
            required: true,
          });
        }
      }
      return result;
    });
  }, []);

  const handleHclChange = (value: string) => {
    setHclTemplate(value);
    syncVariablesFromTemplate(value);
  };

  // Auto-detect variables via backend
  const handleAutoDetect = async () => {
    if (!hclTemplate.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await terraformApi.convertHclToTemplate(hclTemplate);
      setHclTemplate(result.hclTemplate);
      setVariables(result.variables);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Update a single variable
  const updateVariable = (index: number, updates: Partial<TemplateVariable>) => {
    setVariables(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeVariable = (index: number) => {
    const varName = variables[index].name;
    setVariables(prev => prev.filter((_, i) => i !== index));
    // Also remove placeholder from HCL
    setHclTemplate(prev => prev.replace(new RegExp(`\\{\\{${varName}\\}\\}`, 'g'), ''));
  };

  // Load options from linked module for a specific variable
  const loadOptionsFromModule = async (varIndex: number) => {
    if (!linkedModule) return;
    try {
      const moduleVars = await terraformApi.getModuleVariables(linkedModule);
      const varName = variables[varIndex].name;
      const match = moduleVars.find(mv => mv.name === varName);
      if (match?.allowedValues?.length) {
        updateVariable(varIndex, { options: match.allowedValues });
      } else {
        setError(`No enum values found for "${varName}" in module "${linkedModule}"`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Add an option to a variable's options list
  const addOption = (varIndex: number, value: string) => {
    if (!value.trim()) return;
    const v = variables[varIndex];
    const existing = v.options || [];
    if (!existing.includes(value.trim())) {
      updateVariable(varIndex, { options: [...existing, value.trim()] });
    }
    setOptionInput(prev => ({ ...prev, [v.name]: '' }));
  };

  // Remove an option from a variable's options list
  const removeOption = (varIndex: number, optionValue: string) => {
    const v = variables[varIndex];
    updateVariable(varIndex, { options: (v.options || []).filter(o => o !== optionValue) });
  };

  // Dynamic options source helpers
  const updateOptionsSource = (varIndex: number, source: OptionsSource | undefined) => {
    updateVariable(varIndex, { optionsSource: source });
  };

  const loadSchemaResourceTypes = async (versionId: string) => {
    if (schemaResourceTypes[versionId]) return;
    try {
      const types = await terraformApi.listResourceTypes(versionId);
      setSchemaResourceTypes(prev => ({ ...prev, [versionId]: types }));
    } catch {}
  };

  const loadSchemaFields = async (versionId: string, resourceType: string) => {
    const key = `${versionId}:${resourceType}`;
    if (schemaFields[key]) return;
    try {
      const schema = await terraformApi.getResourceSchema(versionId, resourceType);
      const fields = Object.keys(schema.block.attributes || {}).sort();
      setSchemaFields(prev => ({ ...prev, [key]: fields }));
    } catch {}
  };

  const fetchOptionsPreview = async (varName: string, source: OptionsSource) => {
    setPreviewLoading(prev => ({ ...prev, [varName]: true }));
    try {
      const result = await terraformApi.resolveOptions(source);
      setOptionsPreview(prev => ({ ...prev, [varName]: result.options }));
      if (result.message) setError(result.message);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPreviewLoading(prev => ({ ...prev, [varName]: false }));
    }
  };

  const loadSwaggerSchemas = async (endpointId: string) => {
    if (swaggerSchemas[endpointId]) return;
    try {
      const schemas = await terraformApi.listSwaggerSchemas(endpointId);
      setSwaggerSchemas(prev => ({ ...prev, [endpointId]: schemas }));
    } catch (err: any) {
      setError(`Failed to fetch swagger schemas: ${err.message}`);
    }
  };

  const loadSwaggerEnumFields = async (endpointId: string, schemaName: string) => {
    const key = `${endpointId}:${schemaName}`;
    if (swaggerEnumFields[key]) return;
    try {
      const fields = await terraformApi.listSwaggerEnumFields(endpointId, schemaName);
      setSwaggerEnumFields(prev => ({ ...prev, [key]: fields }));
    } catch (err: any) {
      setError(`Failed to fetch enum fields: ${err.message}`);
    }
  };

  const getOptionsSourceLabel = (source?: OptionsSource): string | null => {
    if (!source) return null;
    if (source.type === 'rangeos') {
      return `RangeOS: ${source.resourceType || '?'}.${source.valueField || 'uuid'}`;
    }
    if (source.type === 'rangeos-swagger') {
      return `Swagger: ${source.swaggerSchema || '?'}.${source.swaggerField || '?'}`;
    }
    return `Schema: ${source.resourceType || '?'}.${source.fieldPath || '?'}`;
  };

  // GitLab import
  const loadInstances = async () => {
    try {
      const data = await gitlabApi.listInstances();
      setInstances(data);
      if (data.length === 1) {
        setSelectedInstance(data[0].id);
        loadProjects(data[0].id);
      }
    } catch {}
  };

  const loadProjects = async (instanceId: string) => {
    try {
      const data = await gitlabApi.listProjects(instanceId);
      setProjects(data);
    } catch {}
  };

  const loadBranches = async (instanceId: string, projectId: number) => {
    try {
      const data = await gitlabApi.listBranches(instanceId, projectId);
      setBranches(data);
      const defaultBranch = data.find(b => b.default);
      if (defaultBranch) setSelectedBranch(defaultBranch.name);
    } catch {}
  };

  const browseTree = async (path: string = '') => {
    if (!selectedInstance || !selectedProject) return;
    setTreeLoading(true);
    try {
      const items = await gitlabApi.browseTree(
        selectedInstance,
        selectedProject as number,
        path || undefined,
        selectedBranch || undefined,
      );
      setTreeItems(items);
      setTreePath(path);
    } catch (err: any) {
      setError('Failed to browse repository');
    } finally {
      setTreeLoading(false);
    }
  };

  /** Parse s3cm annotation from HCL content */
  const parseTemplateAnnotation = (content: string): { templateId: string; templateName: string } | null => {
    const idMatch = content.match(/^# s3cm:template_id=(.+)$/m);
    const nameMatch = content.match(/^# s3cm:template_name=(.+)$/m);
    if (idMatch) {
      return { templateId: idMatch[1].trim(), templateName: nameMatch?.[1]?.trim() || 'Unknown' };
    }
    return null;
  };

  /** Strip s3cm annotation lines from HCL content */
  const stripAnnotation = (content: string): string => {
    return content.replace(/^# s3cm:.*\n/gm, '').replace(/^\n/, '');
  };

  const handleImportFile = async (filePath: string) => {
    if (!selectedInstance || !selectedProject) return;
    setLoading(true);
    setError(null);
    setDetectedTemplateId(null);
    setDetectedTemplateName(null);
    try {
      const result = await gitlabApi.getFile(
        selectedInstance,
        selectedProject as number,
        filePath,
        selectedBranch || undefined,
      );

      // Check for template annotation
      const annotation = parseTemplateAnnotation(result.content);
      if (annotation) {
        setDetectedTemplateId(annotation.templateId);
        setDetectedTemplateName(annotation.templateName);
      }

      // Strip annotation before using as template content
      const cleanContent = stripAnnotation(result.content);
      setHclTemplate(cleanContent);
      syncVariablesFromTemplate(cleanContent);
      setSourceFile({
        gitlabInstanceId: selectedInstance,
        projectId: selectedProject as number,
        branch: selectedBranch,
        filePath,
      });
      if (!name) {
        const fileName = filePath.split('/').pop() || filePath;
        setName(fileName.replace(/\.tf$/, '').replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));
      }
      setShowGitLabBrowser(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const openGitLabBrowser = () => {
    setShowGitLabBrowser(true);
    setTreeItems([]);
    setTreePath('');
    setSelectedProject('');
    setSelectedBranch('');
    loadInstances();
  };

  // Save template
  const handleSave = async () => {
    if (!name.trim() || !hclTemplate.trim()) {
      setError('Name and HCL template are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editTemplate) {
        await terraformApi.updateTemplate(editTemplate.id, {
          name: name.trim(),
          description: description.trim() || undefined,
          hclTemplate,
          variables,
          linkedModule: linkedModule || undefined,
          sourceFile,
        });
      } else {
        await terraformApi.createTemplate({
          name: name.trim(),
          description: description.trim() || undefined,
          hclTemplate,
          variables,
          linkedModule: linkedModule || undefined,
          sourceFile,
        });
      }
      onSave();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // GitLab file browser panel
  const renderGitLabBrowser = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => setShowGitLabBrowser(false)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle2">Import .tf File from GitLab</Typography>
      </Box>

      <FormControl fullWidth size="small">
        <InputLabel>GitLab Instance</InputLabel>
        <Select
          value={selectedInstance}
          label="GitLab Instance"
          onChange={e => {
            setSelectedInstance(e.target.value);
            setSelectedProject('');
            setBranches([]);
            setTreeItems([]);
            loadProjects(e.target.value);
          }}
        >
          {instances.map(inst => (
            <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Project</InputLabel>
        <Select
          value={selectedProject}
          label="Project"
          onChange={e => {
            const pid = e.target.value as number;
            setSelectedProject(pid);
            loadBranches(selectedInstance, pid);
          }}
          disabled={!selectedInstance}
        >
          {projects.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.name_with_namespace}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {branches.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Branch</InputLabel>
            <Select
              value={selectedBranch}
              label="Branch"
              onChange={e => {
                setSelectedBranch(e.target.value);
                setTreeItems([]);
                setTreePath('');
              }}
            >
              {branches.map(b => (
                <MenuItem key={b.name} value={b.name}>
                  {b.name}{b.default ? ' (default)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => browseTree('')}
            disabled={!selectedBranch}
          >
            Browse
          </Button>
        </Box>
      )}

      {/* Tree browser */}
      {treeItems.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
          {treePath && (
            <Box
              sx={{ px: 2, py: 0.5, bgcolor: 'action.hover', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
              onClick={() => {
                const parent = treePath.split('/').slice(0, -1).join('/');
                browseTree(parent);
              }}
            >
              <ArrowBackIcon fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {treePath}
              </Typography>
            </Box>
          )}
          {treeLoading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          ) : (
            <List dense disablePadding>
              {treeItems.map(item => (
                <ListItem
                  key={item.path}
                  component="div"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    py: 0.25,
                  }}
                  onClick={() => {
                    if (item.type === 'tree') {
                      browseTree(item.path);
                    } else if (item.name.endsWith('.tf')) {
                      handleImportFile(item.path);
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {item.type === 'tree' ? (
                          <FolderOpenIcon fontSize="small" color="action" />
                        ) : null}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: item.name.endsWith('.tf') ? 600 : 400,
                            color: item.name.endsWith('.tf') ? 'primary.main' : 'text.primary',
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );

  // Main editor
  const renderEditor = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Name + description */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          size="small"
          label="Template Name"
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{ flex: 1 }}
          required
        />
        <TextField
          size="small"
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          sx={{ flex: 2 }}
        />
      </Box>

      {/* Linked module picker */}
      {modules.length > 0 && (
        <FormControl size="small" sx={{ maxWidth: 350 }}>
          <InputLabel>Linked Module</InputLabel>
          <Select
            value={linkedModule}
            label="Linked Module"
            onChange={e => setLinkedModule(e.target.value)}
            startAdornment={linkedModule ? <LinkIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} /> : undefined}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {modules.map(m => (
              <MenuItem key={m.name} value={m.name}>{m.name} ({m.variableCount} vars)</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Two-pane layout */}
      <Box sx={{ display: 'flex', gap: 2, minHeight: 400 }}>
        {/* Left: HCL template */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              HCL Template
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Import from GitLab">
                <Button size="small" startIcon={<UploadIcon />} onClick={openGitLabBrowser}>
                  Import
                </Button>
              </Tooltip>
              <Tooltip title="Auto-detect variables from raw HCL">
                <Button
                  size="small"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleAutoDetect}
                  disabled={!hclTemplate.trim() || loading}
                >
                  Auto-detect
                </Button>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            multiline
            fullWidth
            value={hclTemplate}
            onChange={e => handleHclChange(e.target.value)}
            placeholder={'resource "aws_instance" "example" {\n  ami           = "{{ami_id}}"\n  instance_type = "{{instance_type}}"\n}'}
            sx={{
              flex: 1,
              '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.8rem', alignItems: 'flex-start' },
              '& textarea': { minHeight: '350px !important' },
            }}
          />
        </Box>

        {/* Right: Variables */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
            Variables ({variables.length})
          </Typography>
          <Box
            sx={{
              flex: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'auto',
            }}
          >
            {variables.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No variables yet. Add {'{{placeholders}}'} to your HCL template or click Auto-detect.
                </Typography>
              </Box>
            ) : (
              <List dense disablePadding>
                {variables.map((v, idx) => {
                  const isGenerated = !!v.generatedFrom;
                  // Other variables this one could copy from (exclude self, exclude other generated vars to prevent chains)
                  const sourceOptions = variables.filter((other, otherIdx) => otherIdx !== idx && !other.generatedFrom);
                  return (
                    <React.Fragment key={v.name}>
                      {idx > 0 && <Divider />}
                      <ListItem sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        py: 1,
                        px: 1.5,
                        bgcolor: isGenerated ? 'action.hover' : undefined,
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip
                            label={`{{${v.name}}}`}
                            size="small"
                            color={isGenerated ? 'info' : 'default'}
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          />
                          {isGenerated && (
                            <Typography variant="caption" color="info.main">
                              = {`{{${v.generatedFrom}}}`}
                            </Typography>
                          )}
                          <Box sx={{ flex: 1 }} />
                          <IconButton size="small" onClick={() => removeVariable(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            label="Label"
                            value={v.label}
                            onChange={e => updateVariable(idx, { label: e.target.value })}
                            sx={{ flex: 1 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                              value={v.type}
                              label="Type"
                              onChange={e => updateVariable(idx, { type: e.target.value as TemplateVariable['type'] })}
                            >
                              <MenuItem value="string">string</MenuItem>
                              <MenuItem value="number">number</MenuItem>
                              <MenuItem value="bool">bool</MenuItem>
                              <MenuItem value="list">list</MenuItem>
                              <MenuItem value="map">map</MenuItem>
                              <MenuItem value="hcl">hcl</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Generated From</InputLabel>
                            <Select
                              value={v.generatedFrom || ''}
                              label="Generated From"
                              onChange={e => {
                                const val = e.target.value || undefined;
                                updateVariable(idx, {
                                  generatedFrom: val,
                                  required: val ? false : v.required,
                                });
                              }}
                            >
                              <MenuItem value="">
                                <em>None (manual)</em>
                              </MenuItem>
                              {sourceOptions.map(src => (
                                <MenuItem key={src.name} value={src.name}>
                                  {`{{${src.name}}}`} — {src.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {!isGenerated && (
                            <>
                              <TextField
                                size="small"
                                label="Default"
                                value={v.default ?? ''}
                                onChange={e => updateVariable(idx, { default: e.target.value || undefined })}
                                sx={{ flex: 1 }}
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={v.required}
                                    onChange={e => updateVariable(idx, { required: e.target.checked })}
                                  />
                                }
                                label={<Typography variant="caption">Required</Typography>}
                              />
                            </>
                          )}
                        </Box>
                        {!isGenerated && (
                          <TextField
                            size="small"
                            label="Description"
                            value={v.description || ''}
                            onChange={e => updateVariable(idx, { description: e.target.value || undefined })}
                            fullWidth
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        {/* Options (enum values) editor */}
                        {!isGenerated && (
                          <Box sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">Options</Typography>
                              {linkedModule && (
                                <Tooltip title={`Load enum values from ${linkedModule}`}>
                                  <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<DownloadIcon fontSize="small" />}
                                    onClick={() => loadOptionsFromModule(idx)}
                                    sx={{ fontSize: '0.7rem', py: 0, minHeight: 24 }}
                                  >
                                    Load from module
                                  </Button>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                              {(v.options || []).map(opt => (
                                <Chip
                                  key={opt}
                                  label={opt}
                                  size="small"
                                  onDelete={() => removeOption(idx, opt)}
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                            <TextField
                              size="small"
                              placeholder="Type option + Enter"
                              value={optionInput[v.name] || ''}
                              onChange={e => setOptionInput(prev => ({ ...prev, [v.name]: e.target.value }))}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  addOption(idx, optionInput[v.name] || '');
                                }
                              }}
                              fullWidth
                              sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                            />
                          </Box>
                        )}
                        {/* Dynamic Options Source */}
                        {!isGenerated && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Dynamic Options
                              </Typography>
                              {v.optionsSource && (
                                <Chip
                                  label={getOptionsSourceLabel(v.optionsSource)}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                            <FormControl size="small" fullWidth sx={{ mb: 0.5 }}>
                              <InputLabel>Source Type</InputLabel>
                              <Select
                                value={v.optionsSource?.type || ''}
                                label="Source Type"
                                onChange={e => {
                                  const val = e.target.value;
                                  if (!val) {
                                    updateOptionsSource(idx, undefined);
                                  } else {
                                    updateOptionsSource(idx, { type: val as OptionsSource['type'] });
                                  }
                                }}
                              >
                                <MenuItem value=""><em>None (manual/static)</em></MenuItem>
                                <MenuItem value="rangeos">RangeOS Resource</MenuItem>
                                <MenuItem value="rangeos-swagger">RangeOS Swagger Enum</MenuItem>
                                <MenuItem value="provider-schema">Provider Schema</MenuItem>
                              </Select>
                            </FormControl>

                            {/* RangeOS source config */}
                            {v.optionsSource?.type === 'rangeos' && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Endpoint</InputLabel>
                                  <Select
                                    value={v.optionsSource.endpointId || ''}
                                    label="Endpoint"
                                    onChange={e => updateOptionsSource(idx, { ...v.optionsSource!, endpointId: e.target.value })}
                                  >
                                    {rangeosEndpoints.filter(ep => ep.status === 'active').map(ep => (
                                      <MenuItem key={ep.id} value={ep.id}>{ep.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Resource Type</InputLabel>
                                  <Select
                                    value={v.optionsSource.resourceType || ''}
                                    label="Resource Type"
                                    onChange={e => {
                                      const rt = RANGEOS_RESOURCE_TYPES.find(r => r.key === e.target.value);
                                      updateOptionsSource(idx, {
                                        ...v.optionsSource!,
                                        resourceType: e.target.value,
                                        labelField: rt?.defaultLabel || 'name',
                                        valueField: rt?.defaultValue || 'uuid',
                                      });
                                    }}
                                  >
                                    {RANGEOS_RESOURCE_TYPES.map(rt => (
                                      <MenuItem key={rt.key} value={rt.key}>{rt.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <TextField
                                    size="small"
                                    label="Label Field"
                                    value={v.optionsSource.labelField || ''}
                                    onChange={e => updateOptionsSource(idx, { ...v.optionsSource!, labelField: e.target.value })}
                                    sx={{ flex: 1 }}
                                  />
                                  <TextField
                                    size="small"
                                    label="Value Field"
                                    value={v.optionsSource.valueField || ''}
                                    onChange={e => updateOptionsSource(idx, { ...v.optionsSource!, valueField: e.target.value })}
                                    sx={{ flex: 1 }}
                                  />
                                </Box>
                              </Box>
                            )}

                            {/* Provider Schema source config */}
                            {v.optionsSource?.type === 'provider-schema' && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Schema Version</InputLabel>
                                  <Select
                                    value={v.optionsSource.schemaVersionId || ''}
                                    label="Schema Version"
                                    onChange={e => {
                                      const vid = e.target.value;
                                      updateOptionsSource(idx, { ...v.optionsSource!, schemaVersionId: vid, resourceType: '', fieldPath: '' });
                                      loadSchemaResourceTypes(vid);
                                    }}
                                  >
                                    {schemaVersions.map(sv => (
                                      <MenuItem key={sv.id} value={sv.id}>
                                        {sv.providerSource} v{sv.version} ({sv.resourceCount} resources)
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {v.optionsSource.schemaVersionId && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Resource Type</InputLabel>
                                    <Select
                                      value={v.optionsSource.resourceType || ''}
                                      label="Resource Type"
                                      onChange={e => {
                                        const rt = e.target.value;
                                        updateOptionsSource(idx, { ...v.optionsSource!, resourceType: rt, fieldPath: '' });
                                        loadSchemaFields(v.optionsSource!.schemaVersionId!, rt);
                                      }}
                                    >
                                      {(schemaResourceTypes[v.optionsSource.schemaVersionId] || []).map(rt => (
                                        <MenuItem key={rt} value={rt}>{rt}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                                {v.optionsSource.schemaVersionId && v.optionsSource.resourceType && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Field</InputLabel>
                                    <Select
                                      value={v.optionsSource.fieldPath || ''}
                                      label="Field"
                                      onChange={e => updateOptionsSource(idx, { ...v.optionsSource!, fieldPath: e.target.value })}
                                    >
                                      {(schemaFields[`${v.optionsSource.schemaVersionId}:${v.optionsSource.resourceType}`] || []).map(f => (
                                        <MenuItem key={f} value={f}>{f}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                              </Box>
                            )}

                            {/* RangeOS Swagger source config */}
                            {v.optionsSource?.type === 'rangeos-swagger' && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>RangeOS Endpoint</InputLabel>
                                  <Select
                                    value={v.optionsSource.endpointId || ''}
                                    label="RangeOS Endpoint"
                                    onChange={e => {
                                      const eid = e.target.value;
                                      updateOptionsSource(idx, { ...v.optionsSource!, endpointId: eid, swaggerSchema: '', swaggerField: '' });
                                      loadSwaggerSchemas(eid);
                                    }}
                                  >
                                    {rangeosEndpoints.filter(ep => ep.status === 'active').map(ep => (
                                      <MenuItem key={ep.id} value={ep.id}>{ep.name}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {v.optionsSource.endpointId && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Schema</InputLabel>
                                    <Select
                                      value={v.optionsSource.swaggerSchema || ''}
                                      label="Schema"
                                      onChange={e => {
                                        const schema = e.target.value;
                                        updateOptionsSource(idx, { ...v.optionsSource!, swaggerSchema: schema, swaggerField: '' });
                                        loadSwaggerEnumFields(v.optionsSource!.endpointId!, schema);
                                      }}
                                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                                    >
                                      {(swaggerSchemas[v.optionsSource.endpointId] || []).map(s => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                                {v.optionsSource.endpointId && v.optionsSource.swaggerSchema && (
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Enum Field</InputLabel>
                                    <Select
                                      value={v.optionsSource.swaggerField || ''}
                                      label="Enum Field"
                                      onChange={e => updateOptionsSource(idx, { ...v.optionsSource!, swaggerField: e.target.value })}
                                    >
                                      {(swaggerEnumFields[`${v.optionsSource.endpointId}:${v.optionsSource.swaggerSchema}`] || []).map(f => (
                                        <MenuItem key={f.field} value={f.field}>
                                          {f.field} ({f.values.length} values)
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}
                                {v.optionsSource.endpointId && v.optionsSource.swaggerSchema && (() => {
                                  const enumFieldsKey = `${v.optionsSource!.endpointId}:${v.optionsSource!.swaggerSchema}`;
                                  const fields = swaggerEnumFields[enumFieldsKey] || [];
                                  if (fields.length === 0 && v.optionsSource!.swaggerSchema) {
                                    return (
                                      <Typography variant="caption" color="text.secondary">
                                        No enum fields found in this schema. Try a different schema (e.g. VMSpecification, Chart).
                                      </Typography>
                                    );
                                  }
                                  return null;
                                })()}
                              </Box>
                            )}

                            {/* Fetch Preview button */}
                            {v.optionsSource && (
                              (v.optionsSource.type === 'rangeos' && v.optionsSource.endpointId && v.optionsSource.resourceType) ||
                              (v.optionsSource.type === 'provider-schema' && v.optionsSource.schemaVersionId && v.optionsSource.resourceType && v.optionsSource.fieldPath) ||
                              (v.optionsSource.type === 'rangeos-swagger' && v.optionsSource.endpointId && v.optionsSource.swaggerSchema && v.optionsSource.swaggerField)
                            ) && (
                              <Box sx={{ mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => fetchOptionsPreview(v.name, v.optionsSource!)}
                                  disabled={previewLoading[v.name]}
                                  startIcon={previewLoading[v.name] ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  Fetch Preview
                                </Button>
                                {optionsPreview[v.name] && (
                                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {optionsPreview[v.name].length === 0 ? (
                                      <Typography variant="caption" color="text.secondary">No options found</Typography>
                                    ) : (
                                      optionsPreview[v.name].slice(0, 20).map((opt, i) => (
                                        <Chip
                                          key={i}
                                          label={opt.label === opt.value ? opt.label : `${opt.label} (${opt.value})`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}
                                        />
                                      ))
                                    )}
                                    {optionsPreview[v.name].length > 20 && (
                                      <Typography variant="caption" color="text.secondary">
                                        +{optionsPreview[v.name].length - 20} more
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Box>
      </Box>

      {sourceFile && (
        <Typography variant="caption" color="text.secondary">
          Source: {sourceFile.filePath} ({sourceFile.branch})
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{editTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {detectedTemplateId && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => { setDetectedTemplateId(null); setDetectedTemplateName(null); }}>
            This file was rendered from template <strong>{detectedTemplateName}</strong> ({detectedTemplateId}).
            You can load the original template to make changes using the template form instead of editing raw HCL.
          </Alert>
        )}
        {showGitLabBrowser ? renderGitLabBrowser() : renderEditor()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!showGitLabBrowser && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !name.trim() || !hclTemplate.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? 'Saving...' : editTemplate ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
